import React from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import Text from '~/shared/components/Text';
import { isSavage } from '~/shared/utils/brandNameHelper';
import generateSxfCdnImageUrl from '~/shared/utils/generateSxfCdnImageUrl';
import { Link } from '~/techstyle-shared/next-routes';
import { desktop } from '~/techstyle-shared/react-components';

import BasketItemDetails from '../BasketItemDetails';
import ReviewProductImage from '../ReviewProductImage';

const setId = config.get('public.productTypes.setId');
const IMAGE_PROPS = {
  angle: 'LAYDOWN',
  size: 8,
};

const Wrapper = styled.div`
  padding: ${({ theme }) => theme.space()};
  line-height: 1.5;
  padding-left: 0;

  border-bottom: 1px solid ${({ theme }) => theme.colors.basketItemBorder};

  &:first-child {
    padding-top: ${({ theme }) => theme.space(0)};
  }

  &:last-child {
    border-bottom: none;
  }

  ${({ theme, isMemberCreditApplied }) =>
    theme.context?.cart &&
    `
    background: ${
      isMemberCreditApplied ? theme.colors.boneLavender : 'inherit'
    };
    padding: ${theme.spacing.small}px;
  `}

  ${({ theme }) =>
    theme.context?.summaryDrawer &&
    `
    padding: ${theme.spacing.small}px;
  `}

  ${({ theme }) =>
    theme.context?.checkout &&
    `
    padding: ${theme.spacing.small}px;
  `}

  ${({ theme }) =>
    theme.context?.checkout &&
    !theme.context?.summaryDrawer &&
    !theme.context?.cart &&
    `
    &:first-child {
      border-top: none;
    }
  `}

  ${({ theme }) =>
    theme.context?.cartDrawer &&
    `
    &:first-child {
      border-top: none;
    }
  `}

  ${({ theme }) =>
    theme.context?.orders &&
    `
      padding: ${theme.spacing.small}px;
      ${desktop`
        padding: 0;
      `}
  `}

  ${({ showVipInfo }) =>
    showVipInfo &&
    css`
      border: none;
    `};

  ${({ shouldDisplayUpsellMessage }) =>
    shouldDisplayUpsellMessage &&
    css`
      padding-bottom: ${({ theme }) => theme.spacing.tiny}px;
    `}
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: ${({ theme, noMargin }) =>
    noMargin ? 0 : `${theme.spacing.tiny}px`};

  ${({ theme, isMemberCreditApplied }) =>
    theme.context?.cart &&
    `
    background: ${
      isMemberCreditApplied ? theme.colors.boneLavender : 'inherit'
    };
  `}

  ${({ theme }) =>
    theme.context?.cartDrawer &&
    `
    padding: 0 ${theme.spacing.small}px;
    ${desktop`
      padding: 0;
    `}
  `}
`;

const ImageWrapper = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: 80px;
  flex-direction: column;
  margin-right: 12px;
  background: ${({ theme }) => theme.colors.basketItemImageBackground};

  ${(props) =>
    (props.theme.context?.checkout || props.theme.context?.orders) &&
    desktop`
      flex-basis: 67px;
    `};

  ${(props) =>
    props.theme.context?.cart &&
    desktop`
      flex-basis: 110px;
    `};

  ${(props) =>
    props.theme.context?.vipItem &&
    css`
      flex-basis: 48px;
      ${desktop`flex-basis: 48px;`};
    `};
`;

const ImageNewRatioWrapper = styled(ImageWrapper)`
  flex-basis: 60px;

  ${({ theme }) =>
    theme.context?.cart &&
    desktop`
      flex-basis: 80px;
    `};
`;

const ContentWrapper = styled.div`
  flex: 1;
`;

const ContentRow = styled.div`
  display: flex;
  flex-wrap: no-wrap;
  justify-content: space-between;
  width: 100%;
  flex-direction: row;
  margin-top: ${({ theme }) => theme.sizes.xs}px;
`;

const StyledProductImage = styled(ReviewProductImage).attrs({
  width: 80,
  height: 80,
})`
  width: 100%;
  height: auto;
  vertical-align: top;
  background-color: ${({ theme }) =>
    theme.colors.basketItemProductImageBackground};
`;

const ProductName = styled(Text).attrs({
  forwardedAs: 'h4',
  variant: 'productCellTitle',
})`
  display: inline;
  order: 0;
  color: ${({ theme }) => theme.colors.textDefault};
  margin-bottom: ${({ theme }) => theme.sizes.xs}px;
  ${({ theme }) => theme.typography.variants.reviewsLayoutText.textStyle};
`;

const BasketItem = ({
  discounts,
  extraDetail,
  gwpProduct,
  hideDetails,
  isClearanceItem,
  isExchange,
  isMobile,
  isMysteryBox,
  noLink,
  pSource,
  product,
  renderDetails,
  setMobileReviewProduct,
  shouldDisplayUpsellMessage,
  showVipInfo,
  ...rest
}) => {
  const renderRows = (isProductSet) => {
    // Product data is still stored under each line item currently, so grab it
    // from the first item. This should be further normalized in the future.
    const { bundleItems, label, masterLabel } = product;
    let imageUrl = product.productImage;
    if (isSavage()) {
      imageUrl = generateSxfCdnImageUrl({
        product,
        imageSize: IMAGE_PROPS.size,
        ...IMAGE_PROPS,
      });
    }

    // if mystery box, we want the parent, not the bundle items --- its a secret still
    const rows =
      isProductSet && bundleItems.length && !isMysteryBox
        ? bundleItems
        : [product];

    const handleClick = (e) => {
      if (isMobile) {
        e.preventDefault();
        setMobileReviewProduct(product);
      }
    };

    return rows.map((item, index) => {
      const isGwpProduct = item === gwpProduct;
      const isHeader = index === 0;
      return (
        <Row
          noMargin={isHeader}
          key={`basketitem-${product.productId}-${index}`}
        >
          <ImageNewRatioWrapper>
            {noLink ? (
              <StyledProductImage
                product={item}
                src={
                  imageUrl
                } /* If provided, will override automatic CDN URL */
                {...IMAGE_PROPS}
              />
            ) : (
              <Link
                href="/account/write-review/[reviewId]"
                as={`/account/write-review/${product.id}`}
                passHref
              >
                {/* eslint-disable-next-line */}
              <a onClick={handleClick} tabIndex={-1}>
                  <StyledProductImage
                    product={item}
                    src={
                      imageUrl
                    } /* If provided, will override automatic CDN URL */
                    {...IMAGE_PROPS}
                  />
                </a>
              </Link>
            )}
          </ImageNewRatioWrapper>
          <ContentWrapper>
            <ProductName>
              {noLink ? (
                masterLabel || label
              ) : (
                <Link
                  href="/account/write-review/[reviewId]"
                  as={`/account/write-review/${product.id}`}
                  passHref
                >
                  {/* eslint-disable-next-line */}
                  <a onClick={handleClick} tabIndex={0}>
                    {masterLabel || label}
                  </a>
                </Link>
              )}
            </ProductName>
            <ContentRow>
              <BasketItemDetails
                hideColor={isGwpProduct}
                hideDetails={hideDetails}
                isExchange={isExchange}
                product={item}
                renderDetails={renderDetails}
                discounts={discounts}
                isHeader={isHeader}
                isMysteryBox={isMysteryBox}
                isClearanceItem={isClearanceItem}
              />
            </ContentRow>
            {!isProductSet && isHeader && extraDetail}
          </ContentWrapper>
        </Row>
      );
    });
  };

  // Product data is still stored under each line item currently, so grab it
  // from the first item. This should be further normalized in the future.
  const { bundleItems, productTypeId } = product;

  const isProductSet = !!(productTypeId === setId && bundleItems);

  return (
    <Wrapper
      data-testid="BasketItemWrapper"
      isMemberCreditApplied={product.tokenQtyApplied > 0}
      showVipInfo={showVipInfo}
      shouldDisplayUpsellMessage={shouldDisplayUpsellMessage}
      {...rest}
    >
      {renderRows(isProductSet)}
    </Wrapper>
  );
};

BasketItem.defaultProps = {
  noLink: false,
  shouldDisplayUpsellMessage: false,
};

BasketItem.propTypes = {
  className: PropTypes.string,
  /**
   * Prop passed into  BasketItemDetails
   */
  discounts: PropTypes.array,
  /**
   * The option to pass in extra field (e.g. 'Write a review' link)
   */
  extraDetail: PropTypes.node,
  /**
   * Prop passed into  BasketItemDetails
   */
  gwpProduct: PropTypes.object,
  /**
   * Prop passed into  BasketItemDetails
   */
  hideDetails: PropTypes.bool,
  /**
   * Prop passed into  BasketItemDetails
   */
  isClearanceItem: PropTypes.bool,
  /**
   * Prop passed into  BasketItemDetails
   */
  isExchange: PropTypes.bool,
  isMobile: PropTypes.bool,
  /**
   * Prop passed into  BasketItemDetails
   */
  isMysteryBox: PropTypes.bool,
  /**
   * Turns product label and image into links that directs to review page
   */
  noLink: PropTypes.bool,
  pSource: PropTypes.string,
  product: PropTypes.object.isRequired,
  /**
   * Prop passed into  BasketItemDetails
   */
  renderDetails: PropTypes.func,
  setMobileReviewProduct: PropTypes.func,
  shouldDisplayUpsellMessage: PropTypes.bool,
  showVipInfo: PropTypes.bool,
};

export default BasketItem;
